import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormField,
  Label,
  openDialog,
  Separator,
  useFormContext,
} from 'platform/components';
import {Grid, Heading, HStack, Show, VStack, Text} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';

import {RentalOrderResponseBody} from '@dms/api/rental';
import {RentalVehicleResponseBody, useGetRentalPriceGroupQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {calculatePriceSummary} from '../../../utils/calculatePriceSummary';
import {useGetHandleUpdateChargedKilometers} from '../hooks/useGetHandleUpdateChargedKilometers';
import {useGetReturnVehicleFormDefaultValues} from '../hooks/useGetReturnVehicleFormDefaultValues';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';
import {getPricingTitle} from '../utils/getPricingTitle';
import {getShouldShowInvalidRangeWarning} from '../utils/getShouldShowInvalidRangeWarning';
import {ChangePriceGroupForm} from './ChangePriceGroupForm';
import {ChargedFuelPriceRow} from './ChargedFuelPriceRow';
import {ChargedKilometersPriceRow} from './ChargedKilometersPriceRow';
import {CustomPriceRow} from './CustomPriceRow';
import {DiscountPriceRow} from './DiscountPriceRow';
import {StandardPriceRow} from './StandardPriceRow';

interface PricingProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
  rentalVehicle: RentalVehicleResponseBody | Nullish;
  rentalOrder: RentalOrderResponseBody | Nullish;
}

export function Pricing(props: PricingProps) {
  const {setValues} = useFormContext<ReturnVehicleFormValues>();
  const {tenantCurrency} = useTenant();
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);
  const {defaultValuesFromRent} = useGetReturnVehicleFormDefaultValues(
    props.rentalOrder,
    props.rentalVehicle
  );

  const [handleUpdateChargedKilometers] = useGetHandleUpdateChargedKilometers({
    formApi: props.formApi,
    rentalOrder: props.rentalOrder,
  });

  const [usedPriceGroup, paidDays] = useWatch({
    control: props.control,
    name: ['usedPriceGroup', 'rentPrice.paidDays'],
  });

  const {data: priceGroupData} = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: usedPriceGroup?.rentalPriceGroupId ?? ''},
    {
      skip: isNil(usedPriceGroup?.rentalPriceGroupId),
    }
  );

  const activePriceGroupItem = priceGroupData?.items.find(
    (item) => item.rentalPriceGroupItemId === usedPriceGroup?.rentalPriceGroupItemId
  );

  const updatePricesFromRent = () => {
    const [milageWhenReturning] = props.formApi.getValues(['actualMileage']);

    const pricing = defaultValuesFromRent?.rentPrice;

    setValues({
      'rentPrice.chargedFuel.amount': 0,
      'rentPrice.deliveryPrice': pricing?.deliveryPrice,
      'rentPrice.discount': pricing?.discount,
      'rentPrice.otherPrice': pricing?.otherPrice,
      'rentPrice.pricePerKilometer': pricing?.pricePerKilometer,
      'rentPrice.rentalPrice': pricing?.rentalPrice,
      'rentPrice.dailyRate': pricing?.dailyRate,
      'rentPrice.paidDays': pricing?.paidDays,
      'rentPrice.freeDailyKilometers': pricing?.freeDailyKilometers,
      'rentPrice.totalFreeKilometers': pricing?.totalFreeKilometers,
    });

    handleUpdateChargedKilometers(milageWhenReturning, pricing?.totalFreeKilometers);
  };

  const changePriceGroup = () =>
    openDialog(
      <ChangePriceGroupForm
        formApi={props.formApi}
        control={props.control}
        rentalOrder={props.rentalOrder}
        data-testid={suffixTestId('changePriceGroup', props)}
      />,
      {
        title: i18n.t('page.rentalOrder.labels.changePriceGroup'),
        size: 'large',
        scrollBehavior: 'outside',
      }
    );

  const handleUpdatePricing =
    (updatedField: 'dailyRate' | 'paidDays') => (value: number | null) => {
      const [
        discount,
        dailyRate,
        paidDays,
        rentalPriceVatType,
        freeDailyKilometers,
        actualMileage,
      ] = props.formApi.getValues([
        'rentPrice.discount',
        'rentPrice.dailyRate',
        'rentPrice.paidDays',
        'rentPrice.rentalPrice.vatType',
        'rentPrice.freeDailyKilometers',
        'actualMileage',
      ]);

      const priceVatRate = Number(vatRatesByCode?.[rentalPriceVatType ?? '']?.rate) || 0;
      const rentPrice = match(updatedField)
        .with('dailyRate', () => (value ?? 0) * (paidDays ?? 0))
        .with('paidDays', () => (dailyRate ?? 0) * (value ?? 0))
        .exhaustive();
      const rentPriceWithVat = rentPrice * (1 + priceVatRate / 100);
      const rentPriceVat = rentPriceWithVat - rentPrice;

      const discountPercentage = discount?.percent ?? 0;
      const discountVatType = discount?.discount?.vatType;
      const discountVatRate = Number(vatRatesByCode?.[discountVatType ?? '']?.rate) || 0;

      const newPriceWithoutVat = rentPrice * (discountPercentage / 100);
      const newPriceWithVat = newPriceWithoutVat * (1 + discountVatRate / 100);
      const newVatPrice = newPriceWithVat - newPriceWithoutVat;

      const newTotalFreeKilometers = match(updatedField)
        .with('dailyRate', () => (paidDays ?? 0) * (freeDailyKilometers ?? 0))
        .with('paidDays', () => (freeDailyKilometers ?? 0) * (value ?? 0))
        .exhaustive();

      setValues({
        'rentPrice.rentalPrice.withoutVat': rentPrice,
        'rentPrice.rentalPrice.withVat': rentPriceWithVat,
        'rentPrice.rentalPrice.vat': rentPriceVat,
        'rentPrice.discount.discount.withoutVat': newPriceWithoutVat,
        'rentPrice.discount.discount.withVat': newPriceWithVat,
        'rentPrice.discount.discount.vat': newVatPrice,
        'rentPrice.totalFreeKilometers': newTotalFreeKilometers,
      });

      handleUpdateChargedKilometers(actualMileage, newTotalFreeKilometers);
    };

  const pricingTitle = getPricingTitle(activePriceGroupItem, priceGroupData);
  const showInvalidRangeWarning = getShouldShowInvalidRangeWarning(activePriceGroupItem, paidDays);

  const [discount, ...prices] = useWatch({
    control: props.control,
    name: [
      'rentPrice.discount.discount',
      'rentPrice.rentalPrice',
      'rentPrice.deliveryPrice',
      'rentPrice.coPayment',
      'rentPrice.otherPrice',
      'rentPrice.contractPenalty',
      'rentPrice.chargedKilometer.price',
      'rentPrice.chargedFuel.price',
    ],
  });

  const handleUpdateTotalFreeKilometers = (value: number | null) => {
    const [paidDays, actualMileage] = props.formApi.getValues([
      'rentPrice.paidDays',
      'actualMileage',
    ]);
    const newTotalFreeKilometers = (paidDays ?? 0) * (value ?? 0);

    props.formApi.setValue('rentPrice.totalFreeKilometers', newTotalFreeKilometers);
    handleUpdateChargedKilometers(actualMileage, newTotalFreeKilometers);
  };

  const priceSummary = calculatePriceSummary(prices, discount);

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center">
        <Heading size={4} data-testid={suffixTestId('pricingTitle', props)}>
          {pricingTitle}
        </Heading>

        <ButtonGroup>
          <Button
            variant="link"
            leftIcon="action/cached"
            title={i18n.t('page.rentalOrder.labels.changePriceGroup')}
            data-testid={suffixTestId('changePriceGroup', props)}
            onClick={changePriceGroup}
          />
          <Button
            variant="link"
            leftIcon="action/compare_arrows"
            title={i18n.t('page.rentalOrder.labels.updatePricesFromRent')}
            data-testid={suffixTestId('updatePricesFromRent', props)}
            onClick={updatePricesFromRent}
          />
        </ButtonGroup>
      </HStack>

      <Show when={showInvalidRangeWarning}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('page.rentalOrder.labels.invalidRangeWarning')}
          data-testid={suffixTestId('invalidRangeWarning', props)}
        />
      </Show>

      <Grid columns={4}>
        <FormField
          control={props.control}
          type="currency"
          name="rentPrice.dailyRate"
          decimalPlaces={2}
          label={i18n.t('page.rentalOrder.labels.dailyRate')}
          currency={tenantCurrency}
          onChange={handleUpdatePricing('dailyRate')}
          isRequired
          data-testid={suffixTestId('dailyRate', props)}
        />
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.paidDays"
          label={i18n.t('page.rentalOrder.labels.paidDays')}
          onChange={handleUpdatePricing('paidDays')}
          isRequired
          data-testid={suffixTestId('paidDays', props)}
        />
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.freeDailyKilometers"
          suffix={i18n.t('general.metric.km')}
          onChange={handleUpdateTotalFreeKilometers}
          label={i18n.t('page.rentalOrder.labels.freeDailyKilometers')}
          isRequired
          data-testid={suffixTestId('freeDailyKilometers', props)}
        />
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.totalFreeKilometers"
          isDisabled
          suffix={i18n.t('general.metric.km')}
          label={i18n.t('page.rentalOrder.labels.totalFreeKilometers')}
          data-testid={suffixTestId('totalFreeKilometers', props)}
        />
      </Grid>

      <DiscountPriceRow
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('discountPrice', props)}
      />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.pricePerKilometer"
        withVatLabel={i18n.t('page.rentalOrder.labels.pricePerKilometerWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.pricePerKilometerWithoutVat')}
        data-testid={suffixTestId('pricePerKilometer', props)}
        rentalOrder={props.rentalOrder}
        areFieldsRequired
      />

      <Separator spacing={0} />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.rentalPrice"
        withVatLabel={i18n.t('page.rentalOrder.labels.rentalPriceWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.rentalPriceWithoutVat')}
        data-testid={suffixTestId('rentalPrice', props)}
        rentalOrder={props.rentalOrder}
      />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.deliveryPrice"
        withVatLabel={i18n.t('page.rentalOrder.labels.deliveryPriceWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.deliveryPriceWithoutVat')}
        data-testid={suffixTestId('deliveryPrice', props)}
        rentalOrder={props.rentalOrder}
      />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.otherPrice"
        withVatLabel={i18n.t('page.rentalOrder.labels.otherPriceWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.otherPriceWithoutVat')}
        data-testid={suffixTestId('otherPrice', props)}
        rentalOrder={props.rentalOrder}
      />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.coPayment"
        withVatLabel={i18n.t('page.rentalOrder.labels.coPaymentWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.coPaymentWithoutVat')}
        data-testid={suffixTestId('coPayment', props)}
        rentalOrder={props.rentalOrder}
      />

      <StandardPriceRow
        control={props.control}
        formApi={props.formApi}
        groupName="rentPrice.contractPenalty"
        withVatLabel={i18n.t('page.rentalOrder.labels.contractPenaltyWithVat')}
        withoutVatLabel={i18n.t('page.rentalOrder.labels.contractPenaltyWithoutVat')}
        data-testid={suffixTestId('contractPenalty', props)}
        rentalOrder={props.rentalOrder}
      />

      <ChargedKilometersPriceRow
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('chargedKilometers', props)}
      />

      <ChargedFuelPriceRow
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('chargedFuel', props)}
      />

      <Grid columns={4}>
        <VStack>
          <Label>{i18n.t('page.rentalOrder.labels.totalPriceWithoutVat')}</Label>
          <Text alternative>{formatCurrency(priceSummary.withoutVat, tenantCurrency, 2)}</Text>
        </VStack>

        <VStack>
          <Label>{i18n.t('page.rentalOrder.labels.totalPriceWithVat')}</Label>
          <Text alternative>{formatCurrency(priceSummary.withVat, tenantCurrency, 2)}</Text>
        </VStack>

        <VStack>
          <Label>{`${i18n.t('page.rentalOrder.labels.totalVat')} ${formatCurrencySymbol(tenantCurrency)}`}</Label>
          <Text alternative>{formatCurrency(priceSummary.vat, tenantCurrency, 2)}</Text>
        </VStack>
      </Grid>

      <Separator spacing={0} />

      <FormField
        control={props.control}
        type="switch"
        name="isCustomPriceEnabled"
        label={i18n.t('page.rentalOrder.labels.isCustomPriceEnabled')}
        data-testid={suffixTestId('isCustomPriceEnabled', props)}
      />

      <CustomPriceRow
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('customPrice', props)}
      />
    </VStack>
  );
}
